/**
 * Checklist detail/execution page
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChecklistRun, type ChecklistRunWithItems } from '../services/checklists';
import ChecklistItemDetail from '../components/ChecklistItemDetail';
import '../styles/ChecklistDetail.css';

export default function ChecklistDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [runData, setRunData] = useState<ChecklistRunWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState<any | null>(null);


  useEffect(() => {
    if (id) {
      loadChecklistRun();
    }
  }, [id]);

  const loadChecklistRun = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const data = await getChecklistRun(id);
      setRunData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading checklist');
    } finally {
      setLoading(false);
    }
  };


  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_progress':
        return 'En progreso';
      case 'completed':
        return 'Completado';
      case 'failed':
        return 'Fallido';
      case 'blocked':
        return 'Bloqueado';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'blocked':
        return 'status-blocked';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="checklist-detail">
        <div className="loading">Cargando checklist...</div>
      </div>
    );
  }

  if (!runData) {
    return (
      <div className="checklist-detail">
        <div className="error-message">Checklist no encontrado</div>
        <button className="btn-secondary" onClick={() => navigate('/checklists')}>
          Volver a checklists
        </button>
      </div>
    );
  }

  const { run, items } = runData;
  const completedItems = items.filter((i: { status: string }) => i.status === 'completed').length;
  const totalItems = items.length;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  return (
    <div className="checklist-detail">
      <div className="detail-header">
        <button className="btn-back" onClick={() => navigate('/checklists')}>
          ← Volver
        </button>
        <div className="header-info">
          <h1>Checklist #{run.id.substring(0, 8)}</h1>
          <span className={`status-badge status-${run.status}`}>
            {getStatusLabel(run.status)}
          </span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="progress-card">
        <div className="progress-header">
          <h2>Progreso</h2>
          <span className="progress-text">{completedItems} / {totalItems} completados</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="progress-percentage">{progress}%</div>
      </div>

      <div className="items-section">
        <h2>Items del Checklist</h2>
        <div className="items-list">
              {items.map((item: any, index: number) => (
            <div key={item.id} className="item-card" onClick={() => setSelectedItem(item)}>
              <div className="item-header">
                <span className="item-number">{index + 1}</span>
                <div className="item-title-section">
                  <h3>{item.template_item_name || item.id.substring(0, 8)}</h3>
                  {item.template_item_itc_reference && (
                    <span className="item-itc-reference">{item.template_item_itc_reference}</span>
                  )}
                </div>
                <span className={`status-badge ${getStatusColor(item.status)}`}>
                  {getStatusLabel(item.status)}
                </span>
              </div>
              {item.template_item_description && (
                <div className="item-description">
                  {item.template_item_description}
                </div>
              )}
              {item.notes && (
                <div className="item-notes">
                  <strong>Notas:</strong> {item.notes}
                </div>
              )}
              <div className="item-actions">
                <button
                  className="btn-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedItem(item);
                  }}
                >
                  Ver detalles / Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedItem && runData && (
        <ChecklistItemDetail
          item={selectedItem}
          checklistRunId={runData.run.id}
          installationId={runData.run.installation_id}
          onClose={() => {
            setSelectedItem(null);
            loadChecklistRun();
          }}
          onUpdate={loadChecklistRun}
        />
      )}
    </div>
  );
}

